import React from 'react'
import * as rn from 'react-native'

import Ionicons from 'react-native-vector-icons/Ionicons'

import * as c from '../../common'

import * as gs from '../gStyles'

import Text from './Text'

export type GuidelineProps = c.r<{
  children: false | string
  on: c.Surface
  type?: c.Guideline['type']
}>

const rootStyle: rn.ViewStyle = {
  alignItems: 'center',
  width: '100%',
}

export default React.memo<GuidelineProps>(function Guideline({
  children: text,
  on,
  type = 'info',
}) {
  //#region styling
  const styles = gs.useThemedStyleSheet(themedStyles, on)
  //#endregion styling

  const containerStyle = (() => {
    if (type === 'error') return styles.containerError
    if (type === 'warning') return styles.containerWarning
    return styles.containerInfo
  })()

  const textStyle = (() => {
    if (type === 'error') return styles.textError
    if (type === 'warning') return styles.textWarning
    return styles.textInfo
  })()

  /*
   * How far the badge rides up over the field it belongs to.
   *
   * The badge used to be `position: absolute`, so it took no space at all and
   * therefore always sat OVER whatever followed the field. Fine for a short
   * one-line badge in a normal field gap; it is what Jaron photographed on
   * Customer Info (12896825330), where "Opt no longer avail., reset adder"
   * wrapped to two lines and covered the rep field's value. He caught the same
   * thing a second time, "Turn on Solar in Customer Info" over the Installer
   * toggle - one component, one defect, two screenshots.
   *
   * It is in the layout flow now, pulled up by half its own height. That keeps
   * the look it was reaching for - a chip straddling the bottom edge of its
   * field - while the other half occupies real space, so the next field starts
   * below the badge instead of underneath it. A badge that wraps to two lines
   * now pushes the form down by the extra line rather than swallowing it.
   *
   * The measurement is per-instance. It used to be a module-level `let`,
   * filled in by the FIRST Guideline that ever laid out and then reused by
   * every other one for the rest of the session, so a tall badge rendered
   * early handed its offset to every short badge afterwards.
   */
  const [height, setHeight] = React.useState(0)
  const handleLayout = React.useCallback((e: rn.LayoutChangeEvent): void => {
    const next = e.nativeEvent.layout.height
    setHeight((curr) => (curr === next ? curr : next))
  }, [])
  const style = React.useMemo(
    (): rn.ViewStyle =>
      height ? { ...rootStyle, marginTop: -(height / 2) } : rootStyle,
    [height],
  )

  const icon = (
    <Ionicons
      // Matches the text rather than being hardcoded white: on the two badges
      // where white fails, a white icon fails with it.
      color={(textStyle as rn.TextStyle).color as string}
      name={type === 'info' ? 'information-circle-outline' : 'warning-outline'}
      size={20}
    />
  )

  return (
    <rn.View onLayout={handleLayout} style={style}>
      <rn.View style={containerStyle}>
        <rn.View style={gs.userSelectNone}>{icon}</rn.View>

        <Text style={textStyle}>{text}</Text>
      </rn.View>
    </rn.View>
  )
})

const fontSize = 9
const themedStyles = gs.ThemedStyleSheet.create((t, on) => {
  const container = {
    borderRadius: Number.MAX_SAFE_INTEGER,
    paddingHorizontal: 12,
    maxWidth: '88%' as const,
    ...gs.rowCentered,
  }
  const text = {
    fontFamily: t.fontFamily,
    fontSize,
    textAlign: 'center' as const,
    textAlignVertical: 'center' as const,
    flexShrink: 1,
    flexWrap: 'wrap' as const,
    ...gs.userSelectNone,
  }

  // The text used to be white on every variant. Measured against the colours
  // the themes actually define, two of them are unreadable that way: the light
  // theme's warning #CCCC00 at 1.72:1, which is the badge Jaron photographed,
  // and Nirvana's info #b8a843 at 2.41:1. `readableTextOn` keeps white
  // wherever it clears AA - which is every other variant, including the two
  // that only just clear it - so nothing else changes appearance.
  return {
    containerError: { ...container, backgroundColor: t.danger },
    containerInfo: { ...container, backgroundColor: t[on].highlight },
    containerWarning: { ...container, backgroundColor: t.warning },
    textError: { ...text, color: c.readableTextOn(t.danger) },
    // `?? ''` rather than a fallback colour: a surface with no highlight gives
    // the container no background either, and an unparseable colour is exactly
    // the case `readableTextOn` answers with white - the old behaviour.
    textInfo: { ...text, color: c.readableTextOn(t[on].highlight ?? '') },
    textWarning: { ...text, color: c.readableTextOn(t.warning) },
  }
})
